import { useState } from "react";
import styled from "styled-components/macro";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  BoxNavLeft,
  BoxContent,
  BoxFooter,
  Box,
  BoxNavRight,
  BoxHeader,
  BoxTitle,
  BoxNavButton,
} from "./Box";
import { useSkipioApi, touchSkipioApi } from "../api";
import Button from "./Button";
import Composer from "./Composer";
import Title from "./Title";
import Input, { Label } from "./Input";
import Stack from "./Stack";
import Checkbox from "./Checkbox";
import Combobox from "./Combobox";
import { useFields } from "../hooks/use-fields";
import { useToast } from "../hooks/use-toast";
import SidePanel from "./SidePanel";
import { Close } from "./Icons";
import ContactViewer from "./ContactViewer";
import useExitWarning from "../hooks/use-prevent-exit";
import useMoment from "../hooks/use-moment";
import { caseMethods, transformKey } from "../api/case";
import useLoadAll from "../hooks/use-load-all";
import usePlan from "../hooks/use-plan";
import { IsReadOnlyBoundary } from "../hooks/use-is-readonly";
import { useSkipioQuery } from "../hooks/use-query";
import { fetchUser } from "../api/users";

function ContactEditor({
  contactId = null,
  givenContactValues = {},
  hide,
  onSave,
}) {
  const moment = useMoment();
  const [errors, setErrors] = useState({});
  const skipioApi = useSkipioApi();
  const queryClient = useQueryClient();
  const contact = useSkipioQuery({
    queryKey: ["contacts", contactId],
    enabled: !!contactId,
  }).data || {
    customFields: {},
    isSmsEnabled: true,
    sendBirthdayGreeting: false,
  };

  const plan = usePlan();
  const user = useQuery({
    queryKey: ["users", "me"],
    queryFn: fetchUser,
    select: (queryData) => queryData.data,
  }).data;
  const { data: peers = [] } = useLoadAll(
    plan.isMultiUser ? skipioApi.accountUsers.get : null
  );

  const { customFields } = user;
  const { data: contactFields } = useLoadAll(skipioApi.contactFields, {}, true);

  const contactFieldValuesQuery = useSkipioQuery({
    queryKey: ["contacts", contactId, "contactFieldValues"],
    enabled: !!contactId,
  });
  const contactFieldValues = (contactFieldValuesQuery.data || []).map(
    (value) => {
      return {
        ...value,
        contactField: contactFields.find((f) => f.id === value.contactFieldId),
      };
    }
  );

  const { field, value, touched, reset, checkpoint } = useFields({
    initialValue: {
      firstName: contact.firstName || givenContactValues.firstName || "",
      lastName: contact.lastName || givenContactValues.lastName || "",
      phoneMobile: contact.phoneMobile || givenContactValues.phoneMobile || "",
      email: contact.email || givenContactValues.email || "",
      companyName: contact.companyName || "",
      streetAddress: contact.streetAddress || "",
      city: contact.city || "",
      state: contact.state || "",
      zipCode: contact.zipCode || "",
      campaignIds: Object.keys(contact.campaignIds || {}),
      contactGroupIds: Object.keys(contact.contactGroupIds || {}),
      tags: contact.tags || [],
      isSmsEnabled: contact.isSmsEnabled,
      customFields: Object.fromEntries(
        customFields.map((field) => [
          field.name,
          contact.customFields[transformKey(field.name, caseMethods.camel)] ||
            "",
        ])
      ),
      birthdayGreeting: {
        body: contact.birthdayGreetingBody || "",
        imageUrl: (contact.birthdayGreetingMediaUrls || [""])[0] || "",
        imageType: (contact.birthdayGreetingMediaMimeTypes || [""])[0] || "",
      },
      birthDay: contact.birthDay || null,
      birthMonth: contact.birthMonth || null,
      sendBirthdayGreeting: contact.sendBirthdayGreeting,
      assignedToUserId: contact.assignedToUserId || user.id,
    },
  });

  const hasChanged = Object.keys(touched).length > 0;
  useExitWarning(hasChanged);
  const showToast = useToast();

  async function restoreContact() {
    await skipioApi.batchContacts.put(
      {},
      { data: { restore: true, contactId } }
    );

    showToast(`Restored ${contact.firstName}`);
    await touchSkipioApi("contacts", "addressBook");
    await queryClient.invalidateQueries({ queryKey: ["contacts"] });
    await queryClient.invalidateQueries({ queryKey: ["addressBook"] });
  }

  async function archiveContact() {
    if (
      // eslint-disable-next-line no-restricted-globals
      confirm("Are you sure you would like to archive this contact?")
    ) {
      await skipioApi.contacts[contactId].delete();
      showToast(`Archived ${contact.firstName}`);
      await touchSkipioApi("contacts", "addressBook");
      await queryClient.invalidateQueries({ queryKey: ["contacts"] });
      await queryClient.invalidateQueries({ queryKey: ["addressBook"] });
    }
  }

  async function blockContact() {
    if (
      // eslint-disable-next-line no-restricted-globals
      confirm("Are you sure you would like to block this contact?")
    ) {
      await skipioApi.contacts[contactId].block.put();
      await touchSkipioApi("contacts", "addressBook");
      await queryClient.invalidateQueries({ queryKey: ["contacts"] });
      await queryClient.invalidateQueries({ queryKey: ["addressBook"] });
      showToast(`Blocked ${contact.firstName}`);
    }
  }

  async function unBlockContact() {
    await skipioApi.contacts[contactId].unblock.put();
    await touchSkipioApi("contacts", "addressBook");
    await queryClient.invalidateQueries({ queryKey: ["contacts"] });
    await queryClient.invalidateQueries({ queryKey: ["addressBook"] });
  }

  async function save() {
    if (!hasChanged) return;
    setErrors({});

    const { birthdayGreeting, assignedToUserId, ...payload } = value;

    const caller = contactId
      ? skipioApi.contacts[contactId].put
      : skipioApi.contacts.post;

    const didReassign = contact
      ? // existing contact
        contact.assignedToUserId !== value.assignedToUserId
      : // new contact
        assignedToUserId !== user.id;

    let newContact = null;
    try {
      const {
        data: { data },
      } = await caller(
        {},
        {
          data: {
            contact: {
              ...payload,
              birthdayGreetingBody: birthdayGreeting.body,
              birthdayGreetingMediaUrls: birthdayGreeting.imageUrl
                ? [birthdayGreeting.imageUrl]
                : [],
              birthdayGreetingMediaMimeTypes: birthdayGreeting.imageType
                ? [birthdayGreeting.imageType]
                : [],
            },
          },
        }
      );

      if (didReassign) {
        await skipioApi.contacts.assign.put(
          {},
          {
            data: {
              assignments: [
                {
                  contactId: data.id,
                  userId: assignedToUserId,
                },
              ],
            },
          }
        );
      }

      newContact = data;
    } catch (e) {
      if (e.response) {
        setErrors(e.response.data);
        return;
      } else throw e;
    }

    showToast("Contact Updated", { success: true });
    checkpoint();

    await touchSkipioApi("contacts", "addressBook");
    await queryClient.invalidateQueries({ queryKey: ["contacts"] });
    await queryClient.invalidateQueries({ queryKey: ["addressBook"] });
    if (onSave) onSave(newContact);
  }

  const { field: customFieldsField } = useFields(field("customFields"));

  const isEditing = Boolean(hide);

  return (
    <>
      {isEditing && (
        <BoxHeader>
          <BoxNavLeft>
            <BoxTitle>New Contact</BoxTitle>
          </BoxNavLeft>
          <BoxNavRight>
            <BoxNavButton onClick={hide}>
              <Close />
            </BoxNavButton>
          </BoxNavRight>
        </BoxHeader>
      )}
      <IsReadOnlyBoundary>
        <BoxContent style={{ textAlign: "center" }} dark>
          <Center>
            <Stack space={10}>
              {Boolean(contact.id) && (
                <ProfilePicture title={contact.initials} data-ignore="true" />
              )}
              <Stack horizontal center>
                <ContactName data-ignore="true">
                  {!value.firstName && !value.lastName && "New Contact"}
                  {value.firstName}{" "}
                  <span style={{ fontWeight: "bold" }}>{value.lastName}</span>
                </ContactName>
                {!contact.isSmsBlacklisted && (
                  <div style={{ display: "flex", justifyContent: "flex-end" }}>
                    <Checkbox {...field("isSmsEnabled")}>
                      Send Text Messages
                    </Checkbox>
                  </div>
                )}
              </Stack>
              <>
                <Label>Contact Info:</Label>
                <Box elevated>
                  <BoxContent>
                    <Stack>
                      <Stack horizontal>
                        <Input
                          label="First Name"
                          required
                          {...field("firstName")}
                          error={errors.firstName}
                        />
                        <Input
                          label="Last Name"
                          {...field("lastName")}
                          error={errors.lastName}
                        />
                      </Stack>
                      <Stack horizontal>
                        <Input
                          label="Mobile Number"
                          required
                          {...field("phoneMobile")}
                          error={errors.phoneMobile}
                        />
                        <Input
                          label="Email"
                          {...field("email")}
                          error={errors.email}
                        />
                      </Stack>
                      <Input
                        label="Company"
                        {...field("companyName")}
                        error={errors.companyName}
                      />
                      <Input
                        label="Address"
                        {...field("streetAddress")}
                        error={errors.streetAddress}
                      />
                      <Stack horizontal>
                        <Input
                          label="City"
                          {...field("city")}
                          error={errors.city}
                        />
                        <Input
                          label="State"
                          {...field("state")}
                          error={errors.state}
                        />
                        <Input
                          label="Zip Code"
                          {...field("zipCode")}
                          error={errors.zipCode}
                        />
                      </Stack>
                      {plan.isMultiUser && (
                        <Input
                          label="Assigned To"
                          {...field("assignedToUserId")}
                          error={errors.assignedToUserId}
                          type="select"
                        >
                          <optgroup label="You">
                            <option value={user.id}>
                              {user.firstName} {user.lastName}
                            </option>
                          </optgroup>
                          <optgroup label="Your Team">
                            {peers
                              .filter((p) => p.id !== user.id)
                              .map((user) => (
                                <option
                                  key={user.id}
                                  value={user.id}
                                  disabled={!user.loginEnabled}
                                >
                                  {user.firstName} {user.lastName}
                                </option>
                              ))}
                          </optgroup>
                        </Input>
                      )}
                    </Stack>
                  </BoxContent>
                </Box>
              </>
              {customFields.length !== 0 && (
                <>
                  <Label>Custom Fields:</Label>
                  <Box elevated>
                    <BoxContent>
                      <Stack>
                        {customFields.map((field) => (
                          <Input
                            key={field.name}
                            label={field.label}
                            placeholder="(empty)"
                            {...customFieldsField(field.name)}
                          />
                        ))}
                      </Stack>
                    </BoxContent>
                  </Box>
                </>
              )}
              {contactFieldValues.length !== 0 && (
                <>
                  {Object.entries(
                    contactFieldValues.reduce((result, item) => {
                      const label =
                        item.contactField.remoteSourceLabel || "CRM Fields";
                      return {
                        ...result,
                        [label]: [...(result[label] || []), item],
                      };
                    }, {})
                  ).map(([label, items]) => {
                    return (
                      <>
                        <Label>{label}:</Label>
                        <Box elevated>
                          <BoxContent>
                            <Stack>
                              {items.map((field) =>
                                field.contactField.fieldType === "string" ? (
                                  <Input
                                    rows={1}
                                    type="textarea"
                                    key={field.id}
                                    label={field.contactFieldLabel}
                                    placeholder="(empty)"
                                    readOnly
                                    onChange={() => {}}
                                    value={field.value}
                                    disabled
                                  />
                                ) : (
                                  <Input
                                    key={field.id}
                                    label={field.contactFieldLabel}
                                    placeholder="(empty)"
                                    readOnly
                                    onChange={() => {}}
                                    value={field.value}
                                    disabled
                                  />
                                )
                              )}
                            </Stack>
                          </BoxContent>
                        </Box>
                      </>
                    );
                  })}
                </>
              )}
              <Box elevated>
                <BoxContent>
                  <Stack space={6}>
                    <Combobox
                      placeholder="Search campaigns"
                      label="Campaigns:"
                      endpoint={({ skipioApi }) => skipioApi.campaigns}
                      idToItem={({ skipioApi }, id) =>
                        skipioApi.campaigns[id].get().data
                      }
                      itemToString={(campaign) => campaign.name}
                      {...field("campaignIds")}
                    />
                    <Combobox
                      placeholder="Search groups"
                      label="Groups:"
                      endpoint={({ skipioApi }) => skipioApi.groups}
                      idToItem={({ skipioApi }, id) =>
                        skipioApi.groups[id].get().data
                      }
                      itemToString={(group) => group.name}
                      params={{ userCreated: true }}
                      {...field("contactGroupIds")}
                    />
                    <Combobox
                      addLabel="Add Tag"
                      placeholder="Enter a tag"
                      label="Tags:"
                      allowString
                      {...field("tags")}
                    />
                  </Stack>
                </BoxContent>
              </Box>
              <>
                <Label>Birthday:</Label>
                <Box>
                  <BoxContent>
                    <Stack>
                      <Stack horizontal center>
                        <Input
                          label="Month"
                          type="select"
                          {...field("birthMonth")}
                        >
                          <option value="">-</option>
                          {moment.months().map((name, index) => (
                            <option key={name} value={index + 1}>
                              {name}
                            </option>
                          ))}
                        </Input>
                        <Input label="Day" type="select" {...field("birthDay")}>
                          <option value="">-</option>
                          {Array.from({ length: 31 }, (_, i) => i + 1).map(
                            (number) => (
                              <option key={number} value={number}>
                                {number}
                              </option>
                            )
                          )}
                        </Input>
                      </Stack>
                      <Composer
                        {...field("birthdayGreeting")}
                        enableMessageLibrary={false}
                        enableScheduling={false}
                        enableRinglessVoicemail={false}
                      />
                      <Checkbox {...field("sendBirthdayGreeting")}>
                        Send Birthday Greeting
                      </Checkbox>
                    </Stack>
                  </BoxContent>
                </Box>
              </>
              {contact.id && (
                <ContactActions>
                  <Button
                    variant="link"
                    onClick={
                      contact.isDeleted ? restoreContact : archiveContact
                    }
                  >
                    {contact.isDeleted ? "Restore Contact" : "Archive Contact"}
                  </Button>
                  <Button
                    variant={contact.isBlocked ? "link" : "destroy"}
                    onClick={contact.isBlocked ? unBlockContact : blockContact}
                  >
                    {contact.isBlocked ? "Unblock Contact" : "Block Contact"}
                  </Button>
                </ContactActions>
              )}
            </Stack>
          </Center>
        </BoxContent>
      </IsReadOnlyBoundary>
      <BoxFooter>
        <Center>
          <Stack horizontal center>
            <BoxNavLeft>
              {hasChanged && (
                <Button variant="link" onClick={reset}>
                  Cancel
                </Button>
              )}
            </BoxNavLeft>
            <BoxNavRight>
              <Button
                disabled={!(value.firstName && value.phoneMobile)}
                onClick={save}
              >
                Save
              </Button>
            </BoxNavRight>
          </Stack>
        </Center>
      </BoxFooter>
    </>
  );
}

export function NewContactSidePanel({ trigger, hint, onSave }) {
  return (
    <SidePanel
      trigger={trigger}
      content={({ hide }) => (
        <NewContactSidePanelContent hide={hide} hint={hint} onSave={onSave} />
      )}
    />
  );
}

export function NewContactSidePanelContent({
  hide,
  hint,
  onSave,
  params = {},
}) {
  const { phoneMobile } = params;
  const [contact] =
    useSkipioQuery({
      queryKey: ["addressBook", { query: phoneMobile }],
      enabled: !!phoneMobile,
    }).data || [];

  const [contactId, setContactId] = useState(contact ? contact.id : null);

  return contactId ? (
    <ContactViewer contactId={contactId} hide={hide} />
  ) : (
    <ContactEditor
      hide={hide}
      givenContactValues={
        hint
          ? {
              firstName: isNaN(Number(hint)) ? hint : "",
              phoneMobile: isNaN(Number(hint)) ? "" : hint,
            }
          : params
          ? params
          : undefined
      }
      onSave={(contact) => {
        if (onSave) {
          onSave(contact);
          hide();
        } else setContactId(contact.id);
      }}
    />
  );
}

export default ContactEditor;

const Center = styled.div`
  max-width: 550px;
  text-align: left;
  margin: auto;
`;

const ContactName = styled(Title)`
  font-size: 36px;
  font-weight: 300;
  padding: 20px 0;
`;

const ProfilePicture = styled.div`
  width: 96px;
  height: 96px;
  background: white;
  border-radius: 1000px;
  position: relative;
  background-size: 100%;
  box-shadow: 0 0 0px 1px rgba(0, 0, 0, 0.1), 0 1.5px 3px rgba(0, 0, 0, 0.1);
  overflow: hidden;
  margin-top: 48px;
  &[title]:after {
    display: block;
    content: attr(title);
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    font-size: 24px;
    font-weight: 500;
    color: #676767;
  }
`;

const ContactActions = styled.div`
  display: flex;
`;
